import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResidenceConstructionProgress } from '../../../graphql/graphql';
import {
  CARD_WIDTH,
  CARDS_GAP,
  ConstructionDescription,
  ConstructionTitle,
  ContentItem,
  sliderButtonsStyles,
  VideoSectionContainer,
  VideoSectionContent,
  VideoSectionHead,
  VideSectionTitle
} from './styled';
import { SliderButtons } from '../../NewSection/SliderButtons';
import { PromoVideo } from './PromoVideo';
import { formatDate } from '../../../utils/helpers/common';
import { TIME_FORMAT } from '../../../utils/helpers/constants';
import { GalleryThumbnail } from './GalleryThumbnail';
import { DirectionEnum } from '../../NewSection/types';
import useScreenSize from '../../../hooks/useScreenSize';

interface VideoSectionProps {
  videoId?: string | null | undefined;
  construction?: ResidenceConstructionProgress[];
  name?: string;
  companyName?: string;
  priceMin?: number | null;
  currency?: string;
}

export const VideoSection: React.FC<VideoSectionProps> = memo(
  ({ videoId, construction, name, companyName, priceMin, currency }) => {
    const { t, i18n } = useTranslation();
    const { isSmall } = useScreenSize();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLeftDisabled, setIsLeftDisabled] = useState(true);
    const [isRightDisabled, setIsRightDisabled] = useState(false);

    const scrollContainer = useCallback(
      (direction: DirectionEnum) => {
        const el = containerRef.current;
        if (!el) return;

        let scrollAmount = el.clientWidth;

        if (!isSmall) {
          const CARD_FULL_WIDTH = CARD_WIDTH + CARDS_GAP;

          const cardsPerView = Math.floor(
            (el.clientWidth + CARDS_GAP) / CARD_FULL_WIDTH
          );

          scrollAmount = cardsPerView * CARD_FULL_WIDTH;
        }

        el.scrollBy({
          left: direction === DirectionEnum.left ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });
      },
      [isSmall]
    );

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;

      const handleScroll = () => {
        const maxScrollLeft = el.scrollWidth - el.clientWidth;
        const scrollLeft = Math.round(el.scrollLeft);

        setIsLeftDisabled(scrollLeft <= 0);
        setIsRightDisabled(scrollLeft >= maxScrollLeft);
      };

      handleScroll();

      el.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        el.removeEventListener('scroll', handleScroll);
      };
    }, []);

    const galleryDescription = t('residences.title', { name: companyName });
    const priceInfo = t('residences.priceMin', { priceMin, currency });

    return (
      <VideoSectionContainer>
        <VideoSectionHead>
          <SliderButtons
            isRightButtonDisabled={isRightDisabled}
            isLeftButtonDisabled={isLeftDisabled}
            onLeftButtonClick={() => scrollContainer(DirectionEnum.left)}
            onRightButtonClick={() => scrollContainer(DirectionEnum.right)}
            styles={sliderButtonsStyles}
          />
          <VideSectionTitle tag="title_3_new">
            {t('residences.promo')}
          </VideSectionTitle>
        </VideoSectionHead>

        <VideoSectionContent ref={containerRef}>
          {videoId && (
            <ContentItem>
              <PromoVideo
                videoId={videoId}
                galleryDescription={galleryDescription}
                priceInfo={priceInfo}
              />
              <ConstructionTitle tag="title_4_new">
                {t('residences.promo_video')}
              </ConstructionTitle>
              <ConstructionDescription tag="body_2">
                {name}
              </ConstructionDescription>
            </ContentItem>
          )}
          {construction?.map((item, index) => (
            <ContentItem key={index}>
              {item?.youtubeVideoId && item?.isPromo && (
                <PromoVideo
                  videoId={item.youtubeVideoId}
                  galleryDescription={galleryDescription}
                  priceInfo={priceInfo}
                />
              )}
              {item && !item.isPromo && !!item.photos?.length && (
                <GalleryThumbnail
                  photos={item.photos}
                  galleryDescription={galleryDescription}
                  priceInfo={priceInfo}
                />
              )}

              <ConstructionTitle tag="title_4_new">
                {item?.name}
              </ConstructionTitle>
              <ConstructionDescription tag="body_2">
                {formatDate(item?.date, i18n, TIME_FORMAT.fullDateOnly)}
              </ConstructionDescription>
            </ContentItem>
          ))}
        </VideoSectionContent>
      </VideoSectionContainer>
    );
  }
);

VideoSection.displayName = `ItemVideoSection`;
